import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AmendedCycleService } from './amended-cycle.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-amended-cycle',
    templateUrl: './amended-cycle.component.html',
    styleUrls: ['./amended-cycle.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AmendedCycleComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal properties
  modalTitle = "Amended Cycle";
  isEdit = false;
  showFormLoader = false;

  // Entity data properties
  entityList: any[] = [];
  amendedCycle: any = {};
  amendedCycle_backup: any = {};

  // Data type options
  dataTypeOptions: string[] = ['CODE_COMBINATION', 'ENTITY', 'CHART_ACCT'];

  // Global data properties
  /** Global runtime params (legacy name; flat hydrated params). */
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private amendedCycleService: AmendedCycleService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  init() {
    try {
      // Initialize if we have either filterParams or rowData
      if ((this.filterParams && Object.keys(this.filterParams).length > 0) ||
          (this.rowData && Object.keys(this.rowData).length > 0)) {
        this.setupEntityData();
        this.initializeAmendedCycle();
      }
    } catch (e) {
      console.error('Error in AmendedCycle init:', e);
    }
  }

  private setupEntityData() {
    // Initialize with filter params or rowData
    const taxYear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';

    this.entityList = [
      {key: "Tax Year", value: taxYear},
      {key: "User", value: this.globalService?.user_name || ''},
      {key: "System", value: this.globalService?.system_name || ''}
    ];
  }

  private initializeAmendedCycle() {
    // Initialize with default values, using filterParams for tax year
    const taxYear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';

    this.amendedCycle = {
      TAX_YEAR: taxYear,
      FROM_GROUP_OBJ_KEY: '',
      TO_GROUP_OBJ_KEY: '',
      DATA_TYPE: '',
      CHART_KEY: '',
      ACCT_KEY: '',
      COMBINATION_KEY: '',
      LE_KEY: '',
      ME_KEY: '',
    };

    // If editing existing record
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.isEdit = true;

      // Merge rowData with default structure to ensure all fields exist
      this.amendedCycle = {
        ...this.amendedCycle,
        ...this.rowData
      };
    }

    this.amendedCycle_backup = { ...this.amendedCycle };
  }

  isValid(): boolean {
    // Reset validation flags
    this.amendedCycle.TAX_YEAR_NOT_VALID = false;
    this.amendedCycle.FROM_GROUP_OBJ_KEY_NOT_VALID = false;
    this.amendedCycle.TO_GROUP_OBJ_KEY_NOT_VALID = false;
    this.amendedCycle.DATA_TYPE_NOT_VALID = false;
    this.amendedCycle.CHART_KEY_NOT_VALID = false;
    this.amendedCycle.ACCT_KEY_NOT_VALID = false;
    this.amendedCycle.COMBINATION_KEY_NOT_VALID = false;
    this.amendedCycle.LE_KEY_NOT_VALID = false;
    this.amendedCycle.ME_KEY_NOT_VALID = false;

    let isValid = true;

    if (!this.amendedCycle.TAX_YEAR) {
      this.amendedCycle.TAX_YEAR_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.FROM_GROUP_OBJ_KEY) {
      this.amendedCycle.FROM_GROUP_OBJ_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.TO_GROUP_OBJ_KEY) {
      this.amendedCycle.TO_GROUP_OBJ_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.DATA_TYPE) {
      this.amendedCycle.DATA_TYPE_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.CHART_KEY) {
      this.amendedCycle.CHART_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.ACCT_KEY) {
      this.amendedCycle.ACCT_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.COMBINATION_KEY) {
      this.amendedCycle.COMBINATION_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.LE_KEY) {
      this.amendedCycle.LE_KEY_NOT_VALID = true;
      isValid = false;
    }
    if (!this.amendedCycle.ME_KEY) {
      this.amendedCycle.ME_KEY_NOT_VALID = true;
      isValid = false;
    }

    return isValid;
  }

  save() {
    if (!this.isValid()) {
      this._snackBar.open('Please fill all the required fields', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Prepare data for saving - remove validation flags
    const saveData = { ...this.amendedCycle };

    // Remove validation flag properties before API call
    delete saveData.TAX_YEAR_NOT_VALID;
    delete saveData.FROM_GROUP_OBJ_KEY_NOT_VALID;
    delete saveData.TO_GROUP_OBJ_KEY_NOT_VALID;
    delete saveData.DATA_TYPE_NOT_VALID;
    delete saveData.CHART_KEY_NOT_VALID;
    delete saveData.ACCT_KEY_NOT_VALID;
    delete saveData.COMBINATION_KEY_NOT_VALID;
    delete saveData.LE_KEY_NOT_VALID;
    delete saveData.ME_KEY_NOT_VALID;

    saveData.EDIT_FLAG = this.isEdit ? 'U' : 'I';

    this.amendedCycleService.saveAmendedCycleForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Amended Cycle Updated successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Save error:', error);
      }
    );
  }

  reset() {
    // Reset to original values
    const taxYear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';

    this.amendedCycle = {
      TAX_YEAR: taxYear,
      FROM_GROUP_OBJ_KEY: '',
      TO_GROUP_OBJ_KEY: '',
      DATA_TYPE: '',
      CHART_KEY: '',
      ACCT_KEY: '',
      COMBINATION_KEY: '',
      LE_KEY: '',
      ME_KEY: '',
    };

    // Reset validation flags
    this.amendedCycle.TAX_YEAR_NOT_VALID = false;
    this.amendedCycle.FROM_GROUP_OBJ_KEY_NOT_VALID = false;
    this.amendedCycle.TO_GROUP_OBJ_KEY_NOT_VALID = false;
    this.amendedCycle.DATA_TYPE_NOT_VALID = false;
    this.amendedCycle.CHART_KEY_NOT_VALID = false;
    this.amendedCycle.ACCT_KEY_NOT_VALID = false;
    this.amendedCycle.COMBINATION_KEY_NOT_VALID = false;
    this.amendedCycle.LE_KEY_NOT_VALID = false;
    this.amendedCycle.ME_KEY_NOT_VALID = false;
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
